import { Menu } from "antd";
import { useCallback, useState } from "react";

import { IconCaretSmallDown, IconCaretSmallUp } from "@btnet/blueprint-icons";
import { BTDropdown } from "@btnet/core-components";
import { cn } from "@btnet/utilities-core";

import { BTLocalStorage } from "types/btStorage";
import { BTLoginTypes } from "types/enum";

import { selectJobs } from "utilities/jobPicker/jobPicker";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTMenu } from "commonComponents/btWrappers/BTMenu/BTMenu";
import { type AccountSwitcherItem } from "commonComponents/utilities/JobPicker/JobPicker.api.types";

import "./JobPickerAccountSwitcher.less";

interface IAccountSwitcherMenuItemProps {
    item: AccountSwitcherItem;
    allSubsSelected: boolean;
    onAccountSwitched: (accounts: AccountSwitcherItem[]) => void;
}

const AccountSwitcherMenuItem: React.FunctionComponent<IAccountSwitcherMenuItemProps> = (props) => {
    const { item, onAccountSwitched, ...restProps } = props;
    return (
        <BTMenu.Item
            testid={`accountSwitcher-${item.builderId}`}
            {...restProps}
            onClick={() => onAccountSwitched([item])}
            className={cn({
                Selected: !props.allSubsSelected && item.selected,
                InSession: item.inSession,
            })}
        >
            <div>
                <div>{item.builderName}</div>
                <div className="text-xs text-gray-70">{item.name}</div>
            </div>
        </BTMenu.Item>
    );
};

interface IJobPickerAccountSwitcherProps {
    availableAccounts: AccountSwitcherItem[];
    onAccountChange?: (newAccounts: AccountSwitcherItem[]) => Promise<void>;
}

export const JobPickerAccountSwitcher: React.FunctionComponent<IJobPickerAccountSwitcherProps> = (
    props,
) => {
    const { availableAccounts, onAccountChange } = props;
    const [menuOpen, setMenuOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleAccountChange = useCallback(
        async (accounts: AccountSwitcherItem[]) => {
            // No-op if the user picked the already-selected single account.
            const numSelected = availableAccounts.filter((a) => a.selected).length;
            if (numSelected === accounts.length && accounts.every((a) => a.selected)) {
                return;
            }
            setMenuOpen(false);
            if (onAccountChange) {
                setLoading(true);
                BTLocalStorage.remove("bt-boolean-hasUnreadNotifications");
                BTLocalStorage.remove("bt-boolean-hasUnreadChat");
                selectJobs([], false);
                await onAccountChange(accounts);
                setLoading(false);
            }
        },
        [onAccountChange, availableAccounts],
    );

    const userAccounts: AccountSwitcherItem[] = [];
    const subAccounts: AccountSwitcherItem[] = [];
    let numSubsAvailable = 0;
    let numSubsSelected = 0;
    availableAccounts.forEach((a) => {
        (a.loginType === BTLoginTypes.BUILDER ? userAccounts : subAccounts).push(a);
        numSubsAvailable += a.inSession ? 1 : 0;
        numSubsSelected += a.selected ? 1 : 0;
    });

    const showBoth = userAccounts.length > 0 && subAccounts.length > 0;
    const menu = (
        <BTMenu testid="jobpicker-accountswitcher">
            {(showBoth || userAccounts.length > 1) && (
                <Menu.ItemGroup title="Your internal user accounts">
                    {userAccounts.map((a, i) => (
                        <AccountSwitcherMenuItem
                            key={`users_${i}`}
                            item={a}
                            onAccountSwitched={handleAccountChange}
                            allSubsSelected={false}
                        />
                    ))}
                </Menu.ItemGroup>
            )}
            {(showBoth || subAccounts.length > 1) && (
                <Menu.ItemGroup title="Builders you work with">
                    {numSubsAvailable > 1 && (
                        <BTMenu.Item
                            testid="all_subs"
                            key="all_subs"
                            className={cn("All", {
                                Selected: numSubsSelected > 1,
                            })}
                            onClick={() =>
                                handleAccountChange(subAccounts.filter((a) => a.inSession))
                            }
                        >
                            <div>{`All ${numSubsAvailable} builders`}</div>
                        </BTMenu.Item>
                    )}
                    {subAccounts.map((a, i) => (
                        <AccountSwitcherMenuItem
                            key={`subs_${i}`}
                            item={a}
                            onAccountSwitched={handleAccountChange}
                            allSubsSelected={numSubsSelected > 1}
                        />
                    ))}
                </Menu.ItemGroup>
            )}
        </BTMenu>
    );

    return (
        <div className="flex flex-row items-center gap-2 min-w-0 w-full">
            <span className="text-lg font-semibold font-sans min-w-0 wrap-break-word text-gray-90">
                {numSubsSelected > 1
                    ? `All ${numSubsSelected} builders`
                    : availableAccounts.find((a) => a.selected)?.builderName}
            </span>
            {availableAccounts.length > 1 && (
                <BTDropdown
                    testid="accountSwitcherDropdown"
                    overlay={menu}
                    visible={menuOpen}
                    onVisibleChange={setMenuOpen}
                    className="AccountSwitcherDropdown"
                    overlayClassName="AccountSwitcherOverlay"
                >
                    <BTButton
                        data-testid="accountSwitcher"
                        type="tertiary"
                        icon={menuOpen ? <IconCaretSmallUp /> : <IconCaretSmallDown />}
                        loading={loading}
                    />
                </BTDropdown>
            )}
        </div>
    );
};
